/**
 * ZoneRenderer — the shrinking wall.
 *
 * The zone closes to RADIUS ZERO and it decides the last thirty seconds of a
 * wagered match, so "clearly readable" is a requirement and not a polish item.
 * Three cues, deliberately redundant:
 *
 *   1. The WALL — a tall cylinder you can see from anywhere in the arena, drawn
 *      from the inside, scrolling upward so it reads as energy rather than fog.
 *   2. The GROUND RING — where the wall meets the floor, which is the only cue
 *      that survives when the wall is behind you.
 *   3. The TARGET RING — a thin dashed circle at the radius the zone is
 *      contracting toward, visible during the 5s warning. Knowing where the next
 *      circle lands is the difference between rotating and being caught.
 *
 * All three read the server's zone. The client never derives a radius from the
 * clock; it draws the number it was sent.
 */

import * as THREE from 'three/webgpu';
import { Fn, uv, time, sin, smoothstep, mix, float, vec3, uniform, positionWorld } from 'three/tsl';

const WALL_HEIGHT = 70;
const WALL_SEGMENTS = 96;
/** Below this the cylinder degenerates; the closing flare covers the last metre. */
const MIN_DRAW_RADIUS = 0.6;

export class ZoneRenderer {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'zone';
    scene.add(this.group);

    // Calm-state wall brightness. This number veiled the whole game once: at
    // 0.5+ the far half of the additive cylinder adds ~half the calm color to
    // every sky pixel behind it, bloom picks up the base band, and the frame
    // reads as washed-out powder blue (audit: zone.visible=false alone restored
    // v1-level contrast). Calm is a boundary marker, not a light source.
    this._intensity = uniform(0.12);
    this._danger = uniform(0.0);
    // 1 during the final contraction (target radius zero). Drives a slow
    // brightness pulse so the last thirty seconds read as urgent from anywhere.
    this._finale = uniform(0.0);

    this.wall = new THREE.Mesh(
      // Unit radius, scaled per frame — resizing a CylinderGeometry every frame
      // would rebuild a 96-segment buffer 60 times a second.
      new THREE.CylinderGeometry(1, 1, WALL_HEIGHT, WALL_SEGMENTS, 1, true),
      this._wallMaterial()
    );
    this.wall.renderOrder = 5;
    this.group.add(this.wall);

    this.groundRing = new THREE.Mesh(
      new THREE.RingGeometry(0.965, 1, WALL_SEGMENTS),
      this._ringMaterial(0.85)
    );
    this.groundRing.rotation.x = -Math.PI / 2;
    this.groundRing.renderOrder = 6;
    this.group.add(this.groundRing);

    this.targetRing = new THREE.Mesh(
      new THREE.RingGeometry(0.985, 1, WALL_SEGMENTS),
      this._ringMaterial(0.5, true)
    );
    this.targetRing.rotation.x = -Math.PI / 2;
    this.targetRing.renderOrder = 6;
    this.targetRing.visible = false;
    this.group.add(this.targetRing);

    this._floorY = 0.02;
  }

  _wallMaterial() {
    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    // Seen from inside the cylinder, which is where every player is.
    material.side = THREE.BackSide;
    material.blending = THREE.AdditiveBlending;

    const intensity = this._intensity;
    const danger = this._danger;
    const finale = this._finale;

    material.colorNode = Fn(() => {
      // Blue while it is merely a boundary, red once it is doing damage.
      const calm = vec3(0.35, 0.62, 1.0);
      const lethal = vec3(1.0, 0.22, 0.32);
      // Additive blending: brightness IS emissive intensity, so the finale
      // pulse rides the color rather than the (clamped) opacity.
      const pulse = sin(time.mul(3.0)).mul(0.5).add(0.5);
      const surge = finale.mul(pulse).mul(0.9).add(1.0);
      return mix(calm, lethal, danger).mul(surge);
    })();

    material.opacityNode = Fn(() => {
      const v = uv().y;
      // Dense at the base, thinning upward: the wall has to be legible at eye
      // height without becoming a fog bank that hides the fight behind it.
      const vertical = smoothstep(float(0.62), float(0.0), v);
      const scroll = sin(positionWorld.y.mul(0.45).sub(time.mul(2.2))).mul(0.5).add(0.5);
      const bands = scroll.mul(0.35).add(0.65);
      // Eye-level cap: seen from inside, the far side of this cylinder covers
      // most of the frame, and additive opacity above ~0.15 at eye height
      // veils the entire game. The legible read lives in the band hugging the
      // floor (bottom ~4 of 70 units), which sits below the fight and may stay
      // bright — that band is uncapped via `base`.
      const body = vertical.mul(bands).mul(intensity).min(float(0.15));
      const base = smoothstep(float(0.06), float(0.0), v).mul(bands).mul(intensity).mul(0.7);
      return body.add(base);
    })();

    // Additive surfaces must not receive fog: NodeMaterial's fog mixes the
    // output toward the fog color regardless of blending, so a distant additive
    // wall would ADD fog color to the frame instead of fading out.
    material.fog = false;

    return material;
  }

  _ringMaterial(opacity, dashed = false) {
    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.side = THREE.DoubleSide;
    material.blending = THREE.AdditiveBlending;

    const danger = this._danger;
    const finale = this._finale;
    material.colorNode = Fn(() => {
      // Same finale surge as the wall: the ground ring is the only cue left
      // when the wall is behind the camera, so it pulses in step.
      const pulse = sin(time.mul(3.0)).mul(0.5).add(0.5);
      const surge = finale.mul(pulse).mul(0.9).add(1.0);
      return mix(vec3(0.4, 0.7, 1.0), vec3(1.0, 0.25, 0.35), danger).mul(surge);
    })();

    material.opacityNode = dashed
      ? // Rotating dashes mark the ring you are being asked to move to, so it is
        // never confused with the wall you are currently inside.
        Fn(() => {
          const around = uv().x.mul(90).add(time.mul(1.4));
          return smoothstep(float(0.45), float(0.55), sin(around).mul(0.5).add(0.5)).mul(
            float(opacity)
          );
        })()
      : float(opacity);

    // Same as the wall: additive + fog would add fog color, not fade.
    material.fog = false;

    return material;
  }

  setFloorY(y) {
    // 0.045 slots the rings between the arena deck (floor + 0.02) and ability
    // telegraph decals (floor + 0.06): under the deck they vanish, above the
    // telegraphs they would paint over the thing you dodge by.
    this._floorY = y + 0.045;
  }

  /**
   * @param {{centerX:number,centerZ:number,radius:number,targetRadius:number,dps:number}} zone
   * @param {boolean} warning true during the 5s pre-contraction window
   */
  update(zone, warning) {
    const r = Math.max(zone.radius, MIN_DRAW_RADIUS);

    this.wall.visible = zone.radius > MIN_DRAW_RADIUS;
    this.wall.position.set(zone.centerX, this._floorY + WALL_HEIGHT / 2, zone.centerZ);
    this.wall.scale.set(r, 1, r);

    this.groundRing.position.set(zone.centerX, this._floorY, zone.centerZ);
    this.groundRing.scale.set(r, r, 1);

    const showTarget = warning && zone.targetRadius > MIN_DRAW_RADIUS && zone.targetRadius < zone.radius;
    this.targetRing.visible = showTarget;
    if (showTarget) {
      this.targetRing.position.set(zone.centerX, this._floorY, zone.centerZ);
      this.targetRing.scale.set(zone.targetRadius, zone.targetRadius, 1);
    }

    // dps runs 6 → 15 over the match. Normalising against the end value makes
    // the wall visibly angrier as it gets deadlier.
    this._danger.value = Math.min(1, zone.dps / 15);
    // Calm (dps 0, gym and pre-first-contraction): faint boundary marker.
    // Live: bright — but the opacity node caps the eye-level contribution, so
    // "bright" lands in the floor band and the color shift, not a frame veil.
    this._intensity.value = zone.dps > 0 ? 0.9 : 0.12;
    // Final phase: the contraction is heading to radius zero (targetRadius is 0
    // exactly from ZONE_FINAL_START_TICK on) while damage is live. The gym's
    // zone-off placeholder reports dps 0, so it never pulses.
    this._finale.value = zone.dps > 0 && zone.targetRadius <= MIN_DRAW_RADIUS ? 1 : 0;
  }

  /** True when the player's XZ is outside the circle. Drives the HUD warning. */
  isOutside(zone, x, z) {
    const dx = x - zone.centerX;
    const dz = z - zone.centerZ;
    return Math.sqrt(dx * dx + dz * dz) > zone.radius;
  }
}
